<script setup lang="ts">
/**
 * How access to this assistant is decided, and the data owner's
 * confirmation where one is needed (ADR 0053).
 *
 * Lives under "Who can use it" in the assistant editor — beside the groups
 * and people it is about. It used to sit in a second drawer on the
 * Governance tab, one screen away from the list of groups it confirmed.
 *
 * The same confirmation can be recorded from a data source's page, for
 * every assistant that reads it at once; this is the one-assistant view.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref, watch } from "vue";
import {
  AdminError,
  type GovernanceAssistant,
  attestGovernanceAccess,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { ENFORCEMENT_LABELS, accessTone, fmtWhen } from "../utils/governance";
import { adminLink, groupLabel } from "../utils/vocabulary";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ entry: GovernanceAssistant; devUser: string }>();
const emit = defineEmits<{ (e: "updated", a: GovernanceAssistant): void }>();

const toast = useToast();
const { confirm } = useConfirm();

const ac = computed(() => props.entry.access_class);
const groups = computed(() =>
  [
    ...props.entry.acl_scope.map(groupLabel),
    ...(props.entry.teams ?? []).map((t) => `team ${t.name}`),
  ].join(", "),
);

const needsConfirmation = computed(
  () => ac.value?.key === "group_gated" && props.entry.acl_scope.length > 0,
);
/** One confirmation per data source — each from that source's owner. */
const perSource = computed(() => props.entry.access_attestations ?? []);
/** Which source the form is open for. */
const formFor = ref<string | null>(null);

const owner = ref("");
const note = ref("");
const saving = ref(false);
watch(
  () => props.entry.slug,
  () => {
    owner.value = "";
    note.value = "";
  },
);

async function record(): Promise<void> {
  const who = owner.value.trim();
  const row = perSource.value.find((r) => r.source_key === formFor.value);
  if (!who || !row || saving.value) return;
  const ok = await confirm({
    title: `Record the ${row.source} data owner's confirmation?`,
    message: `This records that ${who} has confirmed everyone in ${groups.value} may see ${row.parts.join(", ")} from ${row.source}, and already has that access there. It covers ${row.source} only, and stops counting if who can open the assistant changes.`,
    confirmLabel: "Record",
  });
  if (!ok) return;
  saving.value = true;
  try {
    const updated = await attestGovernanceAccess(
      props.entry.slug,
      { data_owner: who, note: note.value.trim(), source_key: row.source_key },
      props.devUser,
    );
    owner.value = "";
    note.value = "";
    formFor.value = null;
    toast.success("Confirmation recorded");
    emit("updated", updated);
  } catch (e) {
    toast.error(
      "Could not record the confirmation",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    saving.value = false;
  }
}

function groupNames(groups: string[] | undefined): string {
  return (groups ?? []).map((g) => g.replace(/^group:/, "")).join(", ");
}

/** Data sources opens a source by its key or its name. */
function sourceHref(name: string): string {
  return adminLink("data-sources", { source: name });
}
</script>

<template>
  <div v-if="ac" class="review">
    <p class="review__class">
      <StatusChip :tone="accessTone(ac.key)">{{ ac.label }}</StatusChip>
      <span class="small">{{ ac.detail }}</span>
    </p>
    <p v-if="ac.note" class="small warn-text">{{ ac.note }}</p>

    <ul v-if="ac.sources.length" class="review__sources">
      <li v-for="s in ac.sources" :key="`${s.kind}:${s.name}`" class="small">
        <a v-if="s.kind !== 'table'" :href="sourceHref(s.name)">{{ s.name }}</a>
        <code v-else>{{ s.name }}</code>
        <template v-if="s.reach === 'kept_out'">
          — only {{ groupNames(s.groups) }};
          <span class="ok-text">this assistant's users are kept out</span>
        </template>
        <template
          v-else-if="s.reach === 'reached' && s.groups?.includes('group:public')"
        >
          — not locked in the index: everyone who can use this assistant sees
          all of it
        </template>
        <template v-else-if="s.reach === 'reached'">
          — everyone in {{ groupNames(s.groups) }} sees all of it
        </template>
        <template v-else>— {{ ENFORCEMENT_LABELS[s.enforcement] }}</template>
      </li>
    </ul>

    <details class="review__route small">
      <summary>Who approves what for this class</summary>
      <dl>
        <dt>A new assistant like this</dt>
        <dd>{{ ac.route.new_assistant }}</dd>
        <dt>Giving one person access</dt>
        <dd>{{ ac.route.user_access }}</dd>
      </dl>
    </details>

    <div v-if="needsConfirmation" class="review__confirm">
      <h5>Data-owner confirmations</h5>
      <p class="small">
        Everyone in <strong>{{ groups }}</strong> sees all of the restricted
        data below, so each source's own data owner confirms it — the SharePoint
        owner for SharePoint, the Risma owner for Risma.
      </p>
      <ul class="sources">
        <li v-for="r in perSource" :key="r.source_key">
          <div>
            <a :href="sourceHref(r.source_key)">{{ r.source }}</a>
            <span class="muted small"> · {{ r.parts.join(", ") }}</span>
          </div>
          <div class="small">
            <template v-if="r.attested">
              <StatusChip :tone="r.current ? 'ok' : 'warn'">
                {{ r.current ? "Confirmed" : "Changed since" }}
              </StatusChip>
              {{ r.data_owner }}, {{ fmtWhen(r.recorded_at) }}
            </template>
            <span v-else class="muted">Not confirmed</span>
            <button
              v-if="(!r.attested || !r.current) && formFor !== r.source_key"
              class="btn"
              v-write
              @click="formFor = r.source_key"
            >
              Confirm
            </button>
          </div>
          <form
            v-if="formFor === r.source_key"
            class="review__form"
            v-write
            @submit.prevent="record"
          >
            <label>
              {{ r.source }} data owner
              <input
                v-model="owner"
                maxlength="200"
                placeholder="Name and role of who confirmed it"
              />
            </label>
            <label>
              Note
              <input
                v-model="note"
                maxlength="500"
                placeholder="Where — agreement, ticket, mail"
              />
            </label>
            <button
              type="submit"
              class="btn btn--primary"
              :disabled="saving || !owner.trim()"
            >
              {{ saving ? "Recording…" : "Record" }}
            </button>
          </form>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.ok-text {
  color: var(--color-success);
}
.review {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.review__class {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  flex-wrap: wrap;
  margin: 0;
}
.review__sources {
  margin: 0;
  padding-left: 1.1rem;
}
.review__route summary {
  cursor: pointer;
  color: var(--color-muted);
}
.review__route dl {
  margin: 0.4rem 0 0;
}
.review__route dt {
  font-weight: 600;
  margin-top: 0.3rem;
}
.review__route dd {
  margin: 0.1rem 0 0;
}
.review__confirm {
  padding: 0.6rem 0.7rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt);
}
.review__confirm h5 {
  margin: 0 0 0.3rem;
  font-size: 0.85rem;
}
.review__confirm p {
  margin: 0 0 0.4rem;
}
.sources {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.sources li > div {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.6rem;
  align-items: baseline;
}
.sources .btn {
  margin-left: auto;
}
.muted {
  color: var(--color-muted);
}
.review__form {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.5rem;
  align-items: end;
  font-size: 0.85rem;
}
.review__form label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.small {
  font-size: 0.82rem;
}
.warn-text {
  color: var(--color-warn-ink);
}
</style>
